import { COMBO_CARDS, COMBO_COUNT, cloneRange, totalWeight } from './combos';
import { classifyCombo, classRank } from './classify';
import { applyRemoval } from './removal';
import { bucketRange } from './bucket';
import { narrowMultiplier } from './action-model';
export const initialImageState = () => ({
    handsObserved: 0,
    foldToAggression: 0,
    aggressionFaced: 0,
    shownBluffs: 0,
    shownValue: 0,
    voluntaryOpens: 0,
    openOpportunities: 0,
});
/** Fold one observed image event into the running state. Pure: returns a fresh state. */
export const updateImageState = (state, ev) => {
    const s = { ...state };
    switch (ev) {
        case 'hand':
            s.handsObserved++;
            break;
        case 'foldToAggression':
            s.foldToAggression++;
            s.aggressionFaced++;
            break;
        case 'facedAggression':
            s.aggressionFaced++;
            break;
        case 'shownBluff':
            s.shownBluffs++;
            break;
        case 'shownValue':
            s.shownValue++;
            break;
        case 'open':
            s.voluntaryOpens++;
            s.openOpportunities++;
            break;
        case 'openOpportunity':
            s.openOpportunities++;
            break;
        default: break;
    }
    return s;
};
/**
 * Perceived tightness ∈ [0,1] derived from the image. 0.5 = neutral prior (no reads yet). Higher =
 * Hero looks tight/value-heavy (folds a lot, shows value, opens rarely); lower = Hero looks loose/spewy.
 */
export const imageTightness = (s) => {
    const foldRate = s.aggressionFaced > 0 ? s.foldToAggression / s.aggressionFaced : 0.5;
    const shownTotal = s.shownBluffs + s.shownValue;
    const bluffRate = shownTotal > 0 ? s.shownBluffs / shownTotal : 0.5;
    const openRate = s.openOpportunities > 0 ? s.voluntaryOpens / s.openOpportunities : 0.3;
    const t = 0.5
        + 0.30 * (foldRate - 0.5) * 2 // folds a lot ⇒ tighter
        - 0.35 * (bluffRate - 0.5) * 2 // shows bluffs ⇒ looser
        - 0.30 * (openRate - 0.3) / 0.3; // opens a lot ⇒ looser
    return clamp01(t);
};
/** Classes that image bias scales: an observer discounts (or credits) Hero's weak/bluff region. */
const WEAK_CLASSES = new Set([
    'air', 'ace_high', 'weak_draw', 'weak_pair',
]);
const NUTTED_MIN = classRank('two_pair');
/**
 * Compute Hero's perceived range: narrow the opening prior on Hero's observed line through an observer,
 * scaled by the observer's `observance`, then apply the image bias.
 */
export const perceiveHeroRange = (opening, heroLine, observer, image, board, dead = []) => {
    const observance = observer.observance ?? 0.5;
    const boardSet = new Set([...board, ...dead].map((c) => c));
    let out = cloneRange(opening);
    // Narrow on each observed event, blending the multiplier toward 1 by how little the observer updates.
    for (const evt of heroLine) {
        for (let idx = 0; idx < COMBO_COUNT; idx++) {
            const w = out.weights[idx];
            if (w === 0)
                continue;
            const [a, b] = COMBO_CARDS[idx];
            if (boardSet.has(a) || boardSet.has(b))
                continue;
            const { handClass } = classifyCombo([a, b], board);
            const mult = narrowMultiplier(observer, evt, handClass);
            const blended = 1 - observance * (1 - mult); // observance 0 ⇒ 1 (no update); 1 ⇒ full mult
            out.weights[idx] = w * blended;
        }
    }
    // Image bias: reweight Hero's weak/bluff region by how tight/loose Hero looks.
    const tightness = imageTightness(image);
    if (Math.abs(tightness - 0.5) > 1e-9) {
        const factor = clamp01(1 - (tightness - 0.5) * 2 * 0.6); // tight ⇒ <1 (cut weak), loose ⇒ >1
        for (let idx = 0; idx < COMBO_COUNT; idx++) {
            const w = out.weights[idx];
            if (w === 0)
                continue;
            const [a, b] = COMBO_CARDS[idx];
            if (boardSet.has(a) || boardSet.has(b))
                continue;
            const { handClass } = classifyCombo([a, b], board);
            if (WEAK_CLASSES.has(handClass))
                out.weights[idx] = clamp01(w * factor);
        }
    }
    out = applyRemoval(out, [...board, ...dead]);
    // Summarize: top perceived classes by weight, and whether the range is capped.
    const byClass = new Map();
    let nuttedWeight = 0;
    for (let idx = 0; idx < COMBO_COUNT; idx++) {
        const w = out.weights[idx];
        if (w <= 0)
            continue;
        const [a, b] = COMBO_CARDS[idx];
        const { handClass } = classifyCombo([a, b], board);
        byClass.set(handClass, (byClass.get(handClass) ?? 0) + w);
        if (classRank(handClass) >= NUTTED_MIN)
            nuttedWeight += w;
    }
    const total = totalWeight(out);
    const summary = [...byClass.entries()]
        .sort((x, y) => y[1] - x[1])
        .slice(0, 5)
        .map(([handClass, wsum]) => ({ handClass, pct: total > 0 ? round2((wsum / total) * 100) : 0 }));
    return {
        range: out,
        summary,
        capped: nuttedWeight < 0.5, // less than half a combo of two-pair+ ⇒ effectively capped
        buckets: bucketRange(out, board, observer),
    };
};
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const round2 = (x) => Math.round(x * 100) / 100;
//# sourceMappingURL=perceived.js.map